import { lazy, type JSX } from 'react';
import { createSelector } from 'reselect';

import { clearBankID } from '~/components/authentication/bankid/ducks/actions';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { STEP_TYPES, ACTIONTYPES } from '~/components/authentication/wizard/utils/constants';
import { StepProps, StepHeaderProps } from '~/components/authentication/wizard/types';
import BankidStep from '~/components/authentication/playnow/steps/SGA/bankid';
import { ROUTE_WELCOME } from '~/components/router/constants';
import { SessionCriteria } from '~/components/authentication/bankid/bankid.types';

enum StepRoutes {
	bankId = 'bankid',
	bankIdDeposit = 'bankid-deposit',
	bankIdExtra = 'bankid-extra-info',
	bonus = 'bonus',
	deposit = 'deposit',
	depositComplete = 'deposit-complete',
	depositLimits = 'deposit-limits',
	registrationComplete = 'registration-complete',
	tnc = 'terms-conditions',
	welcome = 'welcome',
	privacy = 'privacy-policy'
}

type PossibleRoutes = (typeof StepRoutes)[keyof typeof StepRoutes];

const FLOW: StepRoutes[] = [
	StepRoutes.bankId,
	StepRoutes.bonus,
	StepRoutes.depositLimits,
	StepRoutes.deposit,
	StepRoutes.depositComplete,
	StepRoutes.tnc,
	StepRoutes.privacy,
	StepRoutes.bankIdExtra,
	StepRoutes.bankIdDeposit,
	StepRoutes.registrationComplete,
	StepRoutes.welcome
];

const TRANSLATIONKEYS = [
	'MOBILE_RESPONSIBLE_GAMING_LEARN_MORE',
	'ACCOUNT_VERIFICATION_BANK_ID_BUTTON',
	'MOBILE_LOGIN_LOGIN_BUTTON',
	'MOBILE_REGISTER_HEADER',
	'MOBILE_SEARCH_SKIP',
	'PLAY_NOW_WELCOME',
	'MOBILE_RESPONSIBLE_GAMING_DEPOSIT_LIMITS',
	'BONUS_TITLE_PLAY_NOW',
	'DEPOSIT_TITLE_PLAY_NOW',
	'MOBILE_HELP_PAGE_SUBMENU_TERMS',
	'SIDE_MENU_PRIVACY_POLICY',
	'BANKID_CONTINUE_SAME_DEVICE'
] as const;

interface RootState {
	authenticated: boolean;
	account: {
		isRegistration: boolean;
	};
	bankId: {
		sessionCriteria?: SessionCriteria;
	};
	bonuses: {
		bonusSkipped?: boolean;
	};
	brand: {
		bankIdDepositDetails: boolean;
	};
	browser: {
		lessThan: {
			medium: boolean;
		};
	};
}

interface StateProp extends RootState {
	bonusSkipped: boolean;
	screenMedium: boolean;
	bankidExtra: boolean;
}

const stateSelector = createSelector(
	authenticationSelector,
	(state: RootState) => state.bankId,
	(state: RootState) => state.brand,
	(state: RootState) => state.bonuses,
	(state: RootState) => state.account,
	(state: RootState) => state.browser,
	(authenticated: boolean, bankId, brand, bonuses, account, browser) => ({
		authenticated,
		bankId,
		brand,
		bonusSkipped: bonuses.bonusSkipped,
		account,
		screenMedium: browser.lessThan.medium,
		bankidExtra: bankId?.sessionCriteria?.shouldShowEmail || bankId?.sessionCriteria?.shouldShowPhone
	})
);

type StepMap = Record<PossibleRoutes, (() => JSX.Element) | React.LazyExoticComponent<() => JSX.Element>>;

const STEPMAP: StepMap = {
	[StepRoutes.bankId]: BankidStep,
	[StepRoutes.bonus]: lazy(() => import('~/components/authentication/playnow/steps/SGA/bonus')),
	[StepRoutes.depositLimits]: lazy(() => import('~/components/authentication/playnow/steps/SGA/deposit-limit')),
	[StepRoutes.deposit]: lazy(() => import('~/components/authentication/playnow/steps/SGA/deposit')),
	[StepRoutes.depositComplete]: lazy(() => import('~/components/authentication/playnow/steps/SGA/deposit-complete')),
	[StepRoutes.tnc]: lazy(() => import('~/components/authentication/playnow/steps/SGA/tnc')),
	[StepRoutes.privacy]: lazy(() => import('~/components/authentication/playnow/steps/SGA/privacy-policy')),
	[StepRoutes.bankIdExtra]: lazy(() => import('~/components/authentication/playnow/steps/SGA/bankid-extra')),
	[StepRoutes.bankIdDeposit]: lazy(() => import('~/components/authentication/playnow/steps/SGA/bankid-deposit')),
	[StepRoutes.registrationComplete]: lazy(() => import('~/components/authentication/playnow/steps/SGA/registration')),
	[StepRoutes.welcome]: lazy(() => import('~/components/authentication/playnow/steps/SGA/welcome'))
};

type StepHeaderPropsExt = StepHeaderProps & {
	text: (typeof TRANSLATIONKEYS)[number];
	moreAction?: {
		text: (typeof TRANSLATIONKEYS)[number];
	};
};

type StepPropsExt = StepProps & {
	key: PossibleRoutes;
	header?: null | StepHeaderPropsExt;
	subStep?: Array<PossibleRoutes> | boolean;
};

const getSteps = (props = {} as StateProp): Record<PossibleRoutes, StepPropsExt> => ({
	[StepRoutes.bankId]: {
		key: StepRoutes.bankId,
		header: {
			text: 'BANKID_CONTINUE_SAME_DEVICE',
			action: ACTIONTYPES.close
		},
		subStep: false,
		isValid: () => !props.authenticated && !props.bankId?.sessionCriteria,
		withSuspense: false,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.bonus]: {
		key: StepRoutes.bonus,
		header: {
			text: 'BONUS_TITLE_PLAY_NOW',
			action: ACTIONTYPES.none,
			moreAction: {
				type: ACTIONTYPES.skip,
				text: 'MOBILE_SEARCH_SKIP'
			}
		},
		subStep: false,
		isValid: () => props.account?.isRegistration && !props.bonusSkipped,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: true
	},
	[StepRoutes.depositLimits]: {
		key: StepRoutes.depositLimits,
		header: {
			text: 'MOBILE_RESPONSIBLE_GAMING_DEPOSIT_LIMITS',
			action: props.account.isRegistration && !props.bonusSkipped ? ACTIONTYPES.back : ACTIONTYPES.none,
			moreAction: {
				text: 'MOBILE_RESPONSIBLE_GAMING_LEARN_MORE'
			}
		},
		subStep: true,
		isValid: () => props.bankId?.sessionCriteria?.shouldShowDepositLimitPage as boolean,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.deposit]: {
		key: StepRoutes.deposit,
		header: {
			text: 'DEPOSIT_TITLE_PLAY_NOW',
			action: !props.bonusSkipped ? ACTIONTYPES.back : ACTIONTYPES.none,
			moreAction: {
				text: 'MOBILE_SEARCH_SKIP',
				type: ACTIONTYPES.skip
			}
		},
		subStep: true,
		isValid: () => props.account?.isRegistration,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: true
	},
	[StepRoutes.depositComplete]: {
		key: StepRoutes.depositComplete,
		subStep: false,
		isValid: () => window.location.search.includes('accHistRef'),
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.tnc]: {
		key: StepRoutes.tnc,
		header: {
			text: 'MOBILE_HELP_PAGE_SUBMENU_TERMS',
			action: ACTIONTYPES.none
		},
		subStep: false,
		isValid: () => props.bankId?.sessionCriteria?.shouldShowComplianceMessage as boolean,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false,
		disableTransition: true
	},
	[StepRoutes.privacy]: {
		key: StepRoutes.privacy,
		isValid: () =>
			(!props.account?.isRegistration &&
				props.bankId?.sessionCriteria?.shouldShowPrivacyPolicyMessage) as boolean,
		header: {
			text: 'SIDE_MENU_PRIVACY_POLICY',
			action: ACTIONTYPES.none
		},
		withSuspense: true,
		subStep: false,
		skippable: false,
		type: STEP_TYPES.step
	},
	[StepRoutes.bankIdExtra]: {
		key: StepRoutes.bankIdExtra,
		header: null,
		className: 'u-relative',
		subStep: false,
		isValid: () => props.bankidExtra,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.bankIdDeposit]: {
		key: StepRoutes.bankIdDeposit,
		header: {
			text: 'DEPOSIT_TITLE_PLAY_NOW',
			action: ACTIONTYPES.none,
			moreAction: {
				text: 'MOBILE_SEARCH_SKIP',
				type: ACTIONTYPES.skip
			}
		},
		subStep: false,
		isValid: () =>
			(props.account?.isRegistration &&
				props.brand?.bankIdDepositDetails &&
				props.bankId?.sessionCriteria) as boolean,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: true
	},
	[StepRoutes.registrationComplete]: {
		key: StepRoutes.registrationComplete,
		header: {
			text: 'PLAY_NOW_WELCOME',
			action: ACTIONTYPES.close
		},
		subStep: false,
		isValid: () => props.account?.isRegistration,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	},
	[StepRoutes.welcome]: {
		key: StepRoutes.welcome,
		header: null,
		subStep: false,
		isValid: () =>
			props.bankId?.sessionCriteria?.shouldShowWelcomePageDetails ||
			(props.authenticated && !props.account?.isRegistration),
		url: ROUTE_WELCOME,
		withSuspense: true,
		type: STEP_TYPES.step,
		skippable: false
	}
});

const resetCallbacks = (): (() => void)[] => [clearBankID];

export default {
	FLOW,
	resetCallbacks,
	getSteps,
	stateSelector,
	STEPMAP,
	TRANSLATIONKEYS
};
